import { useState, useCallback, useRef, useMemo } from 'react';

import { useIntl } from 'react-intl';
import moment from 'moment';

import findIndex from 'lodash/findIndex';

import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LoopIcon from '@mui/icons-material/Loop';
import CircularProgress from '@mui/material/CircularProgress';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import { locale } from 'src/config';
import SearchFilterBar, {
  type FilterStateType,
  type QuickFiltersType,
} from 'src/components/SearchFilterBar';
import DataTable from 'src/components/DataTable';
import PageLayout from 'src/components/PageLayout';
import SignatureIcon from 'src/images/Icons/SignatureIcon';
import { forceString } from 'src/utils/datetime';
import StyledTab from 'src/components/StyledTab/StyledTab';
import StyledTabs from 'src/components/StyledTabs/StyledTabs';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import LongTermContractForm from './components/LongTermContractForm';
import CancelLongTermContractForm from './components/CancelLongTermContractForm';

import {
  useGetContracts,
  useDeleteContract,
  useArchiveContract,
  useCreateSignByUcm,
  useDownloadContract,
  useRequestSignature,
  useReinstateContract,
  type ContractParamsType,
  type ContractFiltersType,
  useGetProjectTypes,
} from './api';
import messages from './messages';

import type { DefaultTheme } from '@mui/styles';
import { formatNumber } from 'src/utils/standards';

import { ArchiveButton } from './components/ArchiveButton';
import JobLink from 'src/components/JobLink';
import AdvancedFiltersButton from 'src/components/AdvancedFiltersButton';
import { ContractFilters } from './components/ContractFilters';
import { AdvancedFiltersState } from 'src/components/AdvancedFilters';

const cancelledEndDateSx = { textDecoration: 'line-through' };

const tableTabs: Array<{ value: UCM.ContractStatusType; label: string }> = [
  {
    value: 'all',
    label: 'All',
  },
  {
    value: 'draft',
    label: 'Draft',
  },
  {
    value: 'not_signed',
    label: 'To Be Signed',
  },
  {
    value: 'user_signed',
    label: 'Signed by Student',
  },
  {
    value: 'completely_signed',
    label: 'Completely Signed',
  },
  {
    value: 'active',
    label: 'Active',
  },
  {
    value: 'expired',
    label: 'Expired',
  },
  {
    value: 'cancelled',
    label: 'Cancelled',
  },
  {
    value: 'deleted',
    label: 'Deleted',
  },
];

export default function ContractsPage() {
  const { formatMessage } = useIntl();

  const [selectedContract, setSelectedContract] =
    useState<UCM.ContractType | null>(null);
  const [downloadingContract, setDownloadingContract] = useState({
    contractId: '',
    contractUuid: '',
  });
  const [params, setParams] = useState<Record<string, string | number>>({
    page: 1,
  });
  const [activeTableTab, setActiveTableTab] = useState<UCM.ContractStatusType>(
    tableTabs[0].value,
  );

  const [openAdvancedFilter, setOpenAdvancedFilter] = useState<boolean>(false);
  const [advancedFilters, setAdvancedFilters] = useState<
    AdvancedFiltersState[]
  >([]);

  const { data: projectTypes } = useGetProjectTypes();

  const navTabs: Array<{ value: UCM.ProjectContractType; label: string }> =
    useMemo(() => {
      let defaultTabs = [
        {
          value: 'all',
          label: 'All',
        },
      ];
      if (projectTypes) {
        defaultTabs = [...defaultTabs, ...projectTypes];
      }
      return defaultTabs;
    }, [projectTypes]);

  const [activeTab, setActiveTab] = useState<UCM.ProjectContractType>(
    navTabs[0].value,
  );

  const [filters, setFilters] = useState<ContractFiltersType>({
    job_id: '',
    user_id: '',
    project_id: '',
    status: activeTableTab,
    project_type: activeTab,
  });

  const { mutate: deleteContract } = useDeleteContract();
  const { mutate: createSignByUcm } = useCreateSignByUcm();
  const { mutate: requestSignature } = useRequestSignature();
  const { mutate: reinstateContract } = useReinstateContract();
  const { mutate: archiveContract, isPending: isArchivingContract } =
    useArchiveContract();

  useDownloadContract(
    {
      ...downloadingContract,
      onSettled: () =>
        setDownloadingContract({ contractId: '', contractUuid: '' }),
    },
    { enabled: !!downloadingContract.contractId },
  );

  const { data: contracts, isFetching: isFetchingContracts } = useGetContracts(
    params,
    filters,
    advancedFilters,
  );

  const displayCancelButton = useCallback(
    (contract: UCM.ContractType) =>
      contract.projectTypeCode === 'long_term' &&
      contract.statusCode === 'active' &&
      !!contract.uuid,
    [],
  );

  const searchFilters: Array<QuickFiltersType> = useRef([
    {
      autoFocus: true,
      id: 'user_id',
      type: 'text' as QuickFiltersType['type'],
      placeholder: formatMessage(messages.searchUserIdPlaceholder),
    },
    {
      id: 'job_id',
      type: 'text' as QuickFiltersType['type'],
      placeholder: formatMessage(messages.searchJobIdPlaceholder),
    },
    {
      id: 'project_id',
      type: 'text' as QuickFiltersType['type'],
      placeholder: formatMessage(messages.searchProjectIdPlaceholder),
    },
  ]).current;

  const tabSx = useRef((theme: DefaultTheme) => ({
    width: '100%',
    borderRadius: theme.spacing(0),
  })).current;

  const iconSx = useRef((theme: DefaultTheme) => ({
    color: theme.palette.grey[600],
  })).current;

  const handleDownloadContract = useCallback((contract: UCM.ContractType) => {
    setDownloadingContract({
      contractId: contract.id,
      contractUuid: contract.uuid,
    });
  }, []);

  const handleDeleteContract = useCallback((contract: UCM.ContractType) => {
    deleteContract(contract.uuid);
  }, []);

  const handleEditClick = useCallback(
    (
      contract: UCM.ContractType,
      event: React.MouseEvent<HTMLElement>,
      handleOpen: (event: React.MouseEvent<HTMLElement>) => void,
    ) => {
      handleOpen(event);
      setSelectedContract(contract);
    },
    [],
  );

  const columns: Array<{
    id: keyof UCM.ContractType | string;
    label: string | React.ReactNode;
  }> = useMemo(() => {
    const excludeColumns = [
      ...(activeTab !== 'all' ? ['projectType'] : []),
      ...(activeTableTab.match(/draft|active|expired|cancelled|deleted/)
        ? ['status']
        : []),
      ...(activeTableTab.match(/not_signed|user_signed|completely_signed/)
        ? ['signatureStatus']
        : []),
    ];

    const allColumns = [
      {
        id: 'student',
        label: formatMessage(messages.studentLabel),
        render: (_: Object, { user }: UCM.ContractType) => {
          return `${user.id} - ${user.firstName} ${user.lastName}`;
        },
      },
      {
        id: 'jobId',
        align: 'center',
        label: formatMessage(messages.jobIdLabel),
        render: (_: Object, { jobIds, projectUuids }: UCM.ContractType) => {
          if (!jobIds.length) {
            return <Typography variant="inherit">-</Typography>;
          }
          return (
            <Stack>
              {jobIds.map((jobId, index) => (
                <JobLink
                  key={jobId}
                  jobId={jobId}
                  projectUuid={projectUuids[index]}
                  target="_blank"
                />
              ))}
            </Stack>
          );
        },
      },
      {
        id: 'contractId',
        align: 'center',
        label: formatMessage(messages.contractIdLabel),
        render: (_: Object, { contractNumber }: UCM.ContractType) => {
          return <Typography variant="inherit">{contractNumber}</Typography>;
        },
      },
      {
        id: 'archiveId',
        align: 'center',
        label: formatMessage(messages.archiveIdLabel),
        render: (_: Object, { archiveNumber }: UCM.ContractType) => (
          <Typography variant="inherit"> {archiveNumber || '-'}</Typography>
        ),
      },
      {
        id: 'customer',
        align: 'center',
        label: formatMessage(messages.customerLabel),
        render: (_: Object, { company }: UCM.ContractType) => {
          return <Typography variant="inherit">{company.name}</Typography>;
        },
      },
      {
        id: 'projectType',
        align: 'center',
        label: formatMessage(messages.projectTypeLabel),
        render: (_: Object, { projectTypeCode }: UCM.ContractType) => {
          const projectType =
            projectTypeCode === 'long_term'
              ? formatMessage(messages.longTerm)
              : formatMessage(messages.shortTerm);

          return <Typography variant="inherit">{projectType}</Typography>;
        },
      },
      {
        id: 'startDate',
        align: 'center',
        label: formatMessage(messages.startDateLabel),
        render: (_: Object, { startDate }: UCM.ContractType) => (
          <Typography variant="inherit">
            {startDate ? forceString(startDate) : '-'}
          </Typography>
        ),
      },
      {
        id: 'endDate',
        align: 'center',
        label: formatMessage(messages.endDateLabel),
        render: (endDate: string | null, contract: UCM.ContractType) => {
          const formattedEndDate = endDate ? forceString(endDate) : '-';

          if (contract.noticeDate) {
            const formattedNoticeDate = forceString(contract.noticeDate);
            return (
              <>
                <Typography variant="inherit">{formattedNoticeDate}</Typography>
                <Typography variant="inherit" sx={cancelledEndDateSx}>
                  {formattedEndDate}
                </Typography>
              </>
            );
          }
          return <Typography variant="inherit">{formattedEndDate}</Typography>;
        },
      },
      {
        id: 'cancellationRequestedDate',
        align: 'center',
        label: formatMessage(messages.cancellationRequestedDateLabel),
        render: (
          cancellationRequestedDate: string | null,
          contract: UCM.ContractType,
        ) => {
          if (cancellationRequestedDate) {
            return (
              <>
                <Typography variant="inherit">
                  {forceString(cancellationRequestedDate)}
                </Typography>
                <Typography variant="inherit">
                  ({contract.cancellationSource?.code})
                </Typography>
              </>
            );
          }
          return <Typography variant="inherit">-</Typography>;
        },
      },
      {
        id: 'daysLeft',
        align: 'center',
        label: formatMessage(messages.daysLeftLabel),
        render: (_: Object, { daysLeft }: UCM.ContractType) => {
          return <Typography variant="inherit">{daysLeft}</Typography>;
        },
      },
      // TODO: Add signing method, BE needs to be updated
      // {
      //   id: 'signingMethod',
      //   label: formatMessage(messages.signingMethod),
      //   render: (_: Object, { signatureStatus }: UCM.ContractType) => {
      //     return <Typography variant="inherit">{signatureStatus}</Typography>;
      //   },
      // },
      {
        id: 'userSigningDate',
        align: 'center',
        width: 88,
        label: formatMessage(messages.studentSignedLabel),
        render: (userSigningDate: string | null) => (
          <Typography variant="inherit">
            {userSigningDate ? forceString(userSigningDate) : '-'}
          </Typography>
        ),
      },
      {
        id: 'companySigningDate',
        align: 'center',
        width: 88,
        label: formatMessage(messages.ucmSignedLabel),
        render: (companySigningDate: string | null) => (
          <Typography variant="inherit">
            {companySigningDate ? forceString(companySigningDate) : '-'}
          </Typography>
        ),
      },
      {
        id: 'status',
        align: 'center',
        label: formatMessage(messages.statusLabel),
        render: (_: Object, { statusCode }: UCM.ContractType) => {
          const success = statusCode === 'active';

          return (
            <Typography
              color={success ? 'success' : 'inherit'}
              variant="inherit"
            >
              {statusCode}
            </Typography>
          );
        },
      },
      {
        id: 'signatureStatus',
        align: 'center',
        label: formatMessage(messages.signatureStatusLabel),
        render: (signatureStatus: string) => {
          return <Typography variant="inherit">{signatureStatus}</Typography>;
        },
      },
      {
        id: 'actions',
        label: '',
        width: 0,
        whiteSpace: 'nowrap',
        render: (_: Object, contract: UCM.ContractType) => {
          const {
            uuid,
            user,
            visible,
            statusCode,
            projectTypeCode,
            signatureStatus,
            companySigningDate,
            showReinstatePhysical,
            firstJobApplicationUuid,
          } = contract;

          const name = `${user?.id} - ${user?.firstName} ${user?.lastName}`;
          const downloading = downloadingContract.contractId === contract.id;
          const showRequestSignature =
            !visible &&
            statusCode?.match(/draft|active/) &&
            projectTypeCode !== 'short_term';
          const showSignByUcm =
            !companySigningDate &&
            signatureStatus.match(/user_signed/) &&
            projectTypeCode !== 'short_term';
          const showDelete =
            !statusCode?.match(/deleted/) && projectTypeCode !== 'short_term';
          const reinstate = () => {
            if (projectTypeCode === 'short_term') {
              if (firstJobApplicationUuid) {
                reinstateContract({
                  projectTypeCode,
                  jobApplicationUuid: firstJobApplicationUuid,
                });
              }
              return;
            }

            reinstateContract({ projectTypeCode, uuid });
          };

          return (
            <Grid display="flex" alignItems="center" justifyContent="flex-end">
              {showReinstatePhysical && (
                <ConfirmationDialog
                  message={formatMessage(messages.confirmReinstateMessage)}
                  onConfirm={reinstate}
                >
                  {(handleOpen) => (
                    <Tooltip
                      arrow
                      title={formatMessage(messages.reinstateTooltip)}
                    >
                      <IconButton onClick={handleOpen}>
                        <LoopIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </ConfirmationDialog>
              )}

              {showRequestSignature && (
                <Tooltip
                  arrow
                  title={formatMessage(messages.requestSignatureTooltip)}
                >
                  <IconButton onClick={() => handleRequestSignature(uuid)}>
                    <SendOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {showSignByUcm && (
                <ConfirmationDialog
                  message={formatMessage(messages.confirmSignMessage, {
                    name,
                  })}
                  onConfirm={() => handleSignByUcm(contract.uuid)}
                >
                  {(handleOpen) => (
                    <Tooltip
                      arrow
                      title={formatMessage(messages.signByUcmTooltip)}
                    >
                      <IconButton onClick={handleOpen}>
                        <SignatureIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </ConfirmationDialog>
              )}

              {projectTypeCode !== 'short_term' && (
                <LongTermContractForm
                  create={false}
                  contractUuid={uuid}
                  contract={buildContractProps(selectedContract)}
                  triggerButton={(handleOpen) => (
                    <Tooltip arrow title={formatMessage(messages.editTooltip)}>
                      <IconButton
                        onClick={(event) =>
                          handleEditClick(contract, event, handleOpen)
                        }
                      >
                        <ModeEditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                />
              )}

              <Tooltip arrow title={formatMessage(messages.downloadTooltip)}>
                <IconButton
                  sx={{ pointerEvents: downloading ? 'none' : 'auto' }}
                  onClick={() => handleDownloadContract(contract)}
                >
                  {downloading ? (
                    <CircularProgress size={22} sx={iconSx} />
                  ) : (
                    <FileDownloadOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>

              {displayCancelButton(contract) && (
                <CancelLongTermContractForm contractUuid={contract.uuid} />
              )}

              {showDelete && (
                <ConfirmationDialog
                  maxWidth="sm"
                  onConfirm={() => handleDeleteContract(contract)}
                  message={formatMessage(messages.deleteContractConfirmMessage)}
                >
                  {(handleOpen) => (
                    <Tooltip
                      arrow
                      title={formatMessage(messages.deleteTooltip)}
                    >
                      <IconButton onClick={handleOpen}>
                        <DeleteForeverOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </ConfirmationDialog>
              )}
            </Grid>
          );
        },
      },
    ];

    return [
      ...allColumns.filter((column) => !excludeColumns.includes(column.id)),
    ];
  }, [
    activeTab,
    activeTableTab,
    selectedContract,
    downloadingContract.contractId,
  ]);

  const handleSignByUcm = useCallback((contractUuid: string) => {
    createSignByUcm(contractUuid);
  }, []);

  const handleRequestSignature = useCallback((contractUuid: string) => {
    requestSignature(contractUuid);
  }, []);

  const handlePagination = useCallback((_: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  const handleActiveTabChange = useCallback((tab: UCM.ProjectContractType) => {
    setActiveTab(tab);
    setParams({ page: 1 });
    setFilters((prevFilters) => ({
      ...prevFilters,
      project_type: tab,
    }));
  }, []);

  const handleActiveTableTabChange = useCallback(
    (_: React.SyntheticEvent, tabIndex: number) => {
      const newStatus = tableTabs[tabIndex].value;
      setActiveTableTab(newStatus);
      setParams({ page: 1 });
      setFilters((prevFilters) => ({
        ...prevFilters,
        status: newStatus,
      }));
    },
    [tableTabs],
  );

  const handleSearch = useCallback((filters: FilterStateType) => {
    setParams({ page: 1 });
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...filters,
    }));
  }, []);

  const handleArchive = useCallback((file: File, onSuccess: () => void) => {
    archiveContract(file, { onSuccess });
  }, []);

  const handleAdvancedFilters = useCallback(() => {
    setOpenAdvancedFilter(!openAdvancedFilter);
  }, [openAdvancedFilter]);

  const handleAdvancedFiltersSearch = useCallback(
    (filters: AdvancedFiltersState[]) => {
      setParams({ page: 1 });
      setAdvancedFilters(filters);
      setOpenAdvancedFilter(false);
    },
    [],
  );

  return (
    <PageLayout
      iconName="contracts"
      tabs={navTabs}
      activeTab={activeTab}
      title={formatMessage(messages.title)}
      onActiveTabChange={handleActiveTabChange}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid container size={{ xs: 12, md: 4 }}>
          <SearchFilterBar
            variant="noBorder"
            fields={searchFilters}
            onSearch={handleSearch}
          />
        </Grid>

        <Grid
          alignItems="center"
          size={{ xs: 12, md: 8 }}
          justifyContent={{ xs: 'flex-start', md: 'space-between' }}
          spacing={2}
          container
        >
          <AdvancedFiltersButton
            open={openAdvancedFilter}
            filters={advancedFilters}
            handleOpenFilters={handleAdvancedFilters}
          />
          <Grid container spacing={2}>
            <ArchiveButton
              onArchive={handleArchive}
              archiving={isArchivingContract}
            />
            <LongTermContractForm />
          </Grid>
        </Grid>
        <ContractFilters
          open={openAdvancedFilter}
          handleSearch={handleAdvancedFiltersSearch}
          filters={advancedFilters}
        />
        <Grid size={12}>
          <DataTable
            columns={columns}
            rows={contracts?.data}
            pagination={contracts?.meta}
            onPaginate={handlePagination}
            loading={isFetchingContracts}
            headerTabs={
              <StyledTabs
                sx={tabSx}
                onChange={handleActiveTableTabChange}
                value={findIndex(
                  tableTabs,
                  (tab) => tab.value === activeTableTab,
                )}
              >
                {tableTabs.map((tab) => (
                  <StyledTab key={tab.value} label={tab.label} />
                ))}
              </StyledTabs>
            }
          />
        </Grid>
      </Grid>
    </PageLayout>
  );
}

export const buildContractProps = (
  contract: UCM.ContractType | null,
): ContractParamsType | null => {
  if (!contract) return null;

  return {
    userId: contract.user.id,
    status: contract.statusCode,
    userUuid: contract.user.uuid,
    jobTypeCode: contract.jobType,
    userEmail: contract.user.email,
    taskTypeCode: contract.taskType,
    jobLocationCode: contract.location,
    customerCode: contract.company.uuid,
    tariffGroupCode: contract.tariffCode,
    jobRequirementCode: contract.requirement,
    employmentTypeCode: contract.employmentType,
    wagePerHour: formatNumber(contract.wage, locale),
    certificateCode: contract.certificate?.code ?? '',
    maxDuration: contract.maxDuration?.toString() ?? '',
    unpaidLeave: contract.unpaidLeave?.toString() ?? '',
    monthlyHours: contract.monthlyHours?.toString() ?? '',
    weeklyHours: contract.avgWeeklyHours?.toString() ?? '',
    endDate: moment(contract.endDate, moment.HTML5_FMT.DATE),
    startDate: moment(contract.startDate, moment.HTML5_FMT.DATE),
    userName: `${contract.user.firstName} ${contract.user.lastName}`,
    plannedEndDate: moment(contract.plannedEndDate, moment.HTML5_FMT.DATE),
    latestStartDate: moment(contract.latestStartDate, moment.HTML5_FMT.DATE),
    plannedStartDate: moment(contract.plannedStartDate, moment.HTML5_FMT.DATE),
  };
};
